import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Sparkles, Cpu } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { API, api } from "@/lib/mro";

const Table = ({ table }) => {
  if (!table?.columns?.length) return null;
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted/60">
          <tr className="text-left font-mono uppercase tracking-wider text-muted-foreground">
            {table.columns.map((c) => <th key={c} className="whitespace-nowrap px-3 py-2">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {table.columns.map((c) => <td key={c} className="px-3 py-2 font-mono">{String(r[c] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Copilot({ plant }) {
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [ai, setAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const endRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/copilot/suggestions").then((r) => setSuggestions(r.data.suggestions));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const ask = async (question) => {
    if (!question.trim() || busy) return;
    setQ("");
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    if (!ai) {
      try {
        const r = await api.post("/copilot/query", { question, mode: "rules", plant });
        setMsgs((m) => [...m, { role: "assistant", text: r.data.answer, table: r.data.table, source: "rules", material_id: r.data.material_id }]);
      } catch {
        setMsgs((m) => [...m, { role: "assistant", text: "Query failed. Please retry.", source: "rules" }]);
      }
      setBusy(false);
      return;
    }
    const idx = msgs.length + 1;
    setMsgs((m) => [...m, { role: "assistant", text: "", source: "ai", streaming: true }]);
    try {
      const res = await fetch(`${API}/copilot/stream`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, mode: "ai", plant }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          setMsgs((m) => {
            const c = [...m];
            const a = { ...c[idx] };
            if (ev.type === "delta") a.text = (a.text || "") + ev.content;
            if (ev.type === "grounding") { a.table = ev.grounding.table; a.material_id = ev.grounding.material_id; }
            if (ev.type === "error") a.text = `AI error: ${ev.content}`;
            if (ev.type === "done") a.streaming = false;
            c[idx] = a;
            return c;
          });
        }
      }
    } catch {
      setMsgs((m) => { const c = [...m]; c[idx] = { ...c[idx], text: "AI stream failed.", streaming: false }; return c; });
    }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="copilot-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Operational copilot</h1>
          <p className="text-sm text-muted-foreground">Ask in plain language. Deterministic rules answer known analyses; AI mode reasons over the same grounded dataset.</p>
        </div>
        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2">
          <Cpu className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="ai-mode" className="text-xs">Rules</Label>
          <Switch id="ai-mode" data-testid="ai-mode-toggle" checked={ai} onCheckedChange={setAi} />
          <Label htmlFor="ai-mode" className="flex items-center gap-1 text-xs"><Sparkles className="h-3.5 w-3.5 text-violet-500" />AI (Claude Sonnet 4.6)</Label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="copilot-suggestions">
        {suggestions.map((s, i) => (
          <button key={i} data-testid={`suggestion-${i}`} onClick={() => ask(s)}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground">
            {s}
          </button>
        ))}
      </div>

      <div className="panel min-h-[420px] p-5" data-testid="copilot-thread">
        {msgs.length === 0 && (
          <div className="grid h-[360px] place-items-center text-center">
            <div>
              <Sparkles className="mx-auto h-8 w-8 text-primary" />
              <p className="mt-3 max-w-md text-sm text-muted-foreground">Try “Which spare parts have excess stock across plants but are at risk of stock-out at another location?”</p>
            </div>
          </div>
        )}
        <div className="space-y-4">
          {msgs.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`msg-${i}`}>
              <div className={`max-w-full rounded-xl px-4 py-3 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "border border-border bg-muted/30 w-full"}`}>
                {m.role === "assistant" && (
                  <div className="mb-2 flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-[10px]">{m.source === "ai" ? "AI · claude-sonnet-4-6" : "deterministic engine"}</Badge>
                    {m.streaming && <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" />}
                  </div>
                )}
                <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                <Table table={m.table} />
                {m.material_id && (
                  <Button size="sm" variant="outline" className="mt-3" data-testid={`msg-part360-${i}`} onClick={() => navigate(`/part/${m.material_id}`)}>
                    Open Part 360
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-4 flex gap-2">
        <Input data-testid="copilot-input" className="h-11" placeholder="Ask about excess stock, duplicates, replenishment, safety stock…"
          value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask(q)} />
        <Button className="h-11" data-testid="copilot-send" disabled={busy} onClick={() => ask(q)}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
